// packages/api-client/src/services/admin/adminAgentService.ts
import { getApiClient } from '../../client';
import { handleApiError } from '../../utils/handleApiError';
import type { ServiceResponse, AgentProfile, AgentProfileSummary, AgentResponse, AgentProfileCreatePayload } from '@dopomogai/types';

const ADMIN_AGENTS_PREFIX = '/admin/agents';

/**
 * Service for admin-only management of Agent Profiles.
 */
export const adminAgentService = {
  /**
   * Creates a new agent profile.
   * @param payload The initial data for the new agent.
   * @returns The fully created AgentProfile object.
   */
  createAgent: (payload: AgentProfileCreatePayload): ServiceResponse<AgentProfile> => {
    return getApiClient().post<AgentProfile>(`${ADMIN_AGENTS_PREFIX}/`, payload)
      .then(res => res.data)
      .catch(err => handleApiError(err, 'creating new agent profile'));
  },

  /**
   * Replaces an entire agent profile document. Used for saving major updates.
   * @param agentId The ID of the agent to update.
   * @param payload The complete, updated AgentProfile object.
   * @returns The updated AgentProfile object.
   */
  updateAgent: (agentId: string, payload: AgentProfile): ServiceResponse<AgentProfile> => {
    return getApiClient().put<AgentProfile>(`${ADMIN_AGENTS_PREFIX}/${agentId}`, payload)
      .then(res => res.data)
      .catch(err => handleApiError(err, `updating agent profile '${agentId}'`));
  },
  
  /**
   * Fetches a list of all agent summaries for the admin dashboard.
   * @returns A list of all agent summaries, regardless of visibility status.
   */
  listAllAgents: (): ServiceResponse<AgentProfileSummary[]> => {
    return getApiClient().get<AgentProfileSummary[]>(`${ADMIN_AGENTS_PREFIX}/`)
      .then(res => res.data)
      .catch(err => handleApiError(err, 'listing all admin agent summaries'));
  },

  /**
   * Fetches the full agent profile for editing in the admin panel.
   * @param agentId The ID of the agent to fetch.
   * @returns The complete AgentProfile object.
   */
  getAgentForAdmin: (agentId: string): ServiceResponse<AgentProfile> => {
    return getApiClient().get<AgentProfile>(`${ADMIN_AGENTS_PREFIX}/${agentId}`)
      .then(res => res.data)
      .catch(err => handleApiError(err, `fetching admin agent profile '${agentId}'`));
  },
  
  /**
   * Permanently deletes an agent profile.
   * @param agentId The ID of the agent to delete.
   * @returns A success message.
   */
  deleteAgent: (agentId: string): ServiceResponse<AgentResponse> => {
    return getApiClient().delete<AgentResponse>(`${ADMIN_AGENTS_PREFIX}/${agentId}`)
      .then(res => res.data)
      .catch(err => handleApiError(err, `deleting agent profile '${agentId}'`));
  },
};